import {
	Alert,
	Badge,
	Button,
	Checkbox,
	Flex,
	Group,
	Modal,
	NumberInput,
	Select,
	Stack,
	Text,
	TextInput,
	Textarea,
} from '@mantine/core'
import { IconAlertCircle, IconPlus, IconSearch } from '@tabler/icons-react'
import { MRT_ColumnDef, MRT_PaginationState } from 'mantine-react-table'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { toast } from 'react-toastify'
import {
	CourseAiLearnerRow,
	CourseAiRefund,
	CourseAiRefundResult,
	createCourseAiRefund,
	getCourseAiLearners,
	getCourseAiRefunds,
	markCourseAiRefundRefunded,
	reapplyCourseAiRefundLockout,
	rejectCourseAiRefund,
	unwrapObject,
} from 'src/apis/courseAiAPI'
import TableComponent from 'src/components/TableComponent'
import {
	LOCK_COURSE_LABEL,
	PAYMENT_TYPE_LABELS,
	REFUND_NOTE_LABEL,
	REFUND_SOURCE_LABELS,
	REFUND_STATUS_COLORS,
	REFUND_STATUS_FILTER_OPTIONS,
	REFUND_STATUS_LABELS,
	formatDateTime,
	formatVnd,
	isStaffCreatedRefund,
	lockoutIncomplete,
	paymentStatusText,
	refundAmountError,
	refundIssueText,
	refundLockTag,
} from './learnerDisplay'

const NOTE_MAX_LENGTH = 2000

/**
 * "Lock this course" goes into the request only when it is unticked. A ticked
 * request is the body the server always took, and the server locks by default.
 */
const lockChoice = (locksCourse: boolean): { locks_course?: false } =>
	locksCourse ? {} : { locks_course: false }

const errorMessage = (error: any, fallback: string) =>
	error?.response?.data?.message ||
	error?.response?.data?.results?.message ||
	fallback

const COLUMN_SIZES: Record<string, number> = {
	no: 28,
	learner: 150,
	course_name: 90,
	amount_paid: 110,
	reason: 260,
	requested_at: 110,
	status: 200,
}

/**
 * The refund is recorded at this point. If the server says a step after it did
 * not finish (the class chat, the counts), the lockout is run once more, and
 * staff are told when it still has not finished. A refund that did not lock the
 * course has no such step: the lockout is never run for it.
 */
const confirmLockout = async (response: any, locksCourse: boolean) => {
	const result = unwrapObject<CourseAiRefundResult>(response)
	if (
		!locksCourse ||
		result?.refund?.locks_course === false ||
		result?.effects?.lockout_applied === false
	) {
		toast.success('Refund recorded. The learner keeps access to this course.')
		return
	}
	let effects = result?.effects
	if (result?.refund?.id && lockoutIncomplete(effects)) {
		try {
			effects = unwrapObject<CourseAiRefundResult>(await reapplyCourseAiRefundLockout(result.refund.id))?.effects
		} catch (error) {
			// Reported below: the refund itself is saved.
		}
	}
	if (lockoutIncomplete(effects)) {
		toast.warning(
			'Refund recorded, but the learner may still be in the class chat. Please tell the developer.',
		)
	} else {
		toast.success('Refund recorded. The learner no longer has this course.')
	}
}

function ServerRefusal({ message }: { message: string | null }) {
	if (!message) return null
	return (
		<Alert color="red" icon={<IconAlertCircle size={16} />}>
			{message}
		</Alert>
	)
}

function RequestSummary({ refund }: { refund: CourseAiRefund }) {
	return (
		<Stack spacing={2}>
			<Text size="sm">
				<b>{refund.name || '-'}</b> {refund.phone ? `(${refund.phone})` : ''}
			</Text>
			<Text size="sm">Course: {refund.course_name || '-'}</Text>
			<Text size="sm">
				Amount paid: {formatVnd(refund.amount_paid)} ({refund.transaction_ref})
			</Text>
		</Stack>
	)
}

/** "Refunded": staff enter what was sent back. The transfer itself happens outside the CMS. */
function RefundedDialog({
	refund,
	onClose,
	onDone,
}: {
	refund: CourseAiRefund | null
	onClose: () => void
	onDone: () => void
}) {
	const [amount, setAmount] = useState<number | ''>('')
	const [note, setNote] = useState('')
	const [locksCourse, setLocksCourse] = useState(true)
	const [saving, setSaving] = useState(false)
	const [refusal, setRefusal] = useState<string | null>(null)

	useEffect(() => {
		setAmount(refund ? refund.amount_paid : '')
		setNote('')
		setLocksCourse(true)
		setRefusal(null)
	}, [refund])

	if (!refund) return null
	const amountError = refundAmountError(amount, refund.amount_paid)

	const submit = async () => {
		if (amountError || !note.trim()) return
		setSaving(true)
		setRefusal(null)
		try {
			const response = await markCourseAiRefundRefunded(refund.id, {
				refunded_amount: Number(amount),
				note: note.trim(),
				...lockChoice(locksCourse),
			})
			await confirmLockout(response, locksCourse)
			onDone()
		} catch (error) {
			setRefusal(errorMessage(error, 'Could not record the refund'))
		} finally {
			setSaving(false)
		}
	}

	return (
		<Modal opened onClose={onClose} title="Mark as Refunded" size="md">
			<Stack spacing="sm">
				<RequestSummary refund={refund} />
				<NumberInput
					label="Refunded amount (VND)"
					value={amount}
					onChange={setAmount}
					min={0}
					max={refund.amount_paid}
					thousandsSeparator=","
					hideControls
					error={amount === refund.amount_paid ? undefined : amountError}
					data-autofocus
				/>
				<Textarea
					label={REFUND_NOTE_LABEL}
					placeholder="For example the bank transfer reference"
					value={note}
					onChange={(event) => setNote(event.currentTarget.value)}
					maxLength={NOTE_MAX_LENGTH}
					minRows={2}
					autosize
					withAsterisk
				/>
				<Checkbox
					label={LOCK_COURSE_LABEL}
					checked={locksCourse}
					onChange={(event) => setLocksCourse(event.currentTarget.checked)}
					disabled={saving}
				/>
				<Text size="xs" color="dimmed">
					{locksCourse ? (
						<>
							This only records a refund the team has already sent outside the CMS. The learner is
							locked out of this course, leaves the class chat and the leaderboard, and can study
							again only by buying the course again. It cannot be undone here.
						</>
					) : (
						<>
							This only records a refund the team has already sent outside the CMS and subtracts it
							from the revenue. The learner keeps access to this course and stays in the class chat
							and on the leaderboard. This payment cannot get another refund record later, and the
							lock cannot be added later.
						</>
					)}
				</Text>
				<ServerRefusal message={refusal} />
				<Group position="right">
					<Button variant="default" onClick={onClose} disabled={saving}>
						Cancel
					</Button>
					<Button
						color="red"
						onClick={submit}
						loading={saving}
						disabled={!!amountError || !note.trim()}
					>
						Confirm Refunded
					</Button>
				</Group>
			</Stack>
		</Modal>
	)
}

function RejectDialog({
	refund,
	onClose,
	onDone,
}: {
	refund: CourseAiRefund | null
	onClose: () => void
	onDone: () => void
}) {
	const [note, setNote] = useState('')
	const [saving, setSaving] = useState(false)
	const [refusal, setRefusal] = useState<string | null>(null)

	useEffect(() => {
		setNote('')
		setRefusal(null)
	}, [refund])

	if (!refund) return null

	const submit = async () => {
		if (!note.trim()) return
		setSaving(true)
		setRefusal(null)
		try {
			await rejectCourseAiRefund(refund.id, { note: note.trim() })
			toast.success('Refund request rejected')
			onDone()
		} catch (error) {
			setRefusal(errorMessage(error, 'Could not reject the request'))
		} finally {
			setSaving(false)
		}
	}

	return (
		<Modal opened onClose={onClose} title="Reject refund request" size="md">
			<Stack spacing="sm">
				<RequestSummary refund={refund} />
				<Textarea
					label={REFUND_NOTE_LABEL}
					placeholder="Why the request is rejected"
					value={note}
					onChange={(event) => setNote(event.currentTarget.value)}
					maxLength={NOTE_MAX_LENGTH}
					minRows={2}
					autosize
					withAsterisk
					data-autofocus
				/>
				<Text size="xs" color="dimmed">
					The learner keeps the course. A rejected request cannot be changed again; the learner can
					send a new one.
				</Text>
				<ServerRefusal message={refusal} />
				<Group position="right">
					<Button variant="default" onClick={onClose} disabled={saving}>
						Cancel
					</Button>
					<Button color="gray" onClick={submit} loading={saving} disabled={!note.trim()}>
						Confirm Reject
					</Button>
				</Group>
			</Stack>
		</Modal>
	)
}

/**
 * "New refund": for a learner who asked through chat and never sent a request
 * in the app. Staff pick the learner and course, then the payment, and it is
 * saved as Refunded straight away.
 */
function NewRefundDialog({
	opened,
	onClose,
	onDone,
}: {
	opened: boolean
	onClose: () => void
	onDone: () => void
}) {
	const [search, setSearch] = useState('')
	const [results, setResults] = useState<CourseAiLearnerRow[]>([])
	const [searching, setSearching] = useState(false)
	const [learner, setLearner] = useState<CourseAiLearnerRow | null>(null)
	const [paymentId, setPaymentId] = useState<string | null>(null)
	const [amount, setAmount] = useState<number | ''>('')
	const [note, setNote] = useState('')
	const [locksCourse, setLocksCourse] = useState(true)
	const [saving, setSaving] = useState(false)
	const [refusal, setRefusal] = useState<string | null>(null)

	useEffect(() => {
		if (!opened) return
		setSearch('')
		setResults([])
		setLearner(null)
		setPaymentId(null)
		setAmount('')
		setNote('')
		setLocksCourse(true)
		setRefusal(null)
	}, [opened])

	// The learners list search, a moment after typing stops.
	useEffect(() => {
		const text = search.trim()
		if (!opened || learner || text.length < 2) {
			setResults([])
			return undefined
		}
		let active = true
		const handle = setTimeout(async () => {
			setSearching(true)
			try {
				const response = await getCourseAiLearners({ search: text, learner_type: 'learner', limit: 20 })
				const rows = unwrapObject<{ rows: CourseAiLearnerRow[] }>(response)?.rows || []
				// Only a row with a real payment has something to refund.
				if (active) setResults(rows.filter((row) => row.payments?.length))
			} catch (error) {
				if (active) setResults([])
			} finally {
				if (active) setSearching(false)
			}
		}, 400)
		return () => {
			active = false
			clearTimeout(handle)
		}
	}, [opened, search, learner])

	const payments = learner?.payments || []
	const payment = payments.find((p) => p.payment_id === paymentId) || null
	const amountError = payment ? refundAmountError(amount, payment.amount) : null

	const choose = (row: CourseAiLearnerRow) => {
		// The newest payment first: it is the one a refund is usually for.
		const latest = row.payments[row.payments.length - 1]
		setLearner(row)
		setPaymentId(latest?.payment_id || null)
		setAmount(latest ? latest.amount : '')
		setLocksCourse(true)
		setRefusal(null)
	}

	const submit = async () => {
		if (!learner || !payment?.payment_id || amountError || !note.trim()) return
		setSaving(true)
		setRefusal(null)
		try {
			const response = await createCourseAiRefund({
				user_id: learner.user_id,
				course_id: learner.course_id,
				payment_id: payment.payment_id,
				refunded_amount: Number(amount),
				note: note.trim(),
				...lockChoice(locksCourse),
			})
			await confirmLockout(response, locksCourse)
			onDone()
		} catch (error) {
			setRefusal(errorMessage(error, 'Could not record the refund'))
		} finally {
			setSaving(false)
		}
	}

	return (
		<Modal opened={opened} onClose={onClose} title="New refund" size="lg">
			<Stack spacing="sm">
				{!learner ? (
					<>
						<TextInput
							label="Learner"
							placeholder="Name or phone"
							icon={<IconSearch size={16} />}
							value={search}
							onChange={(event) => setSearch(event.currentTarget.value)}
							data-autofocus
						/>
						{searching ? <Text size="sm">Searching...</Text> : null}
						{!searching && search.trim().length >= 2 && results.length === 0 ? (
							<Text size="sm" color="dimmed">
								No learner with a payment matches.
							</Text>
						) : null}
						<Stack spacing={6}>
							{results.map((row) => (
								<Button
									key={row.id}
									variant="default"
									onClick={() => choose(row)}
									styles={{ inner: { justifyContent: 'flex-start' } }}
								>
									{row.name || '-'} {row.phone ? `(${row.phone})` : ''} · {row.course_name || '-'} ·{' '}
									{paymentStatusText(row)}
								</Button>
							))}
						</Stack>
					</>
				) : (
					<>
						<Flex justify="space-between" align="center" gap={10}>
							<Stack spacing={2}>
								<Text size="sm">
									<b>{learner.name || '-'}</b> {learner.phone ? `(${learner.phone})` : ''}
								</Text>
								<Text size="sm">Course: {learner.course_name || '-'}</Text>
							</Stack>
							<Button variant="subtle" compact onClick={() => setLearner(null)} disabled={saving}>
								Change learner
							</Button>
						</Flex>
						<Select
							label="Payment"
							data={payments
								.filter((p) => p.payment_id)
								.map((p) => ({
									value: p.payment_id as string,
									label: `${formatDateTime(p.paid_at)} · ${formatVnd(p.amount)} · ${
										PAYMENT_TYPE_LABELS[p.type] || p.type
									}`,
								}))}
							value={paymentId}
							onChange={(value) => {
								setPaymentId(value)
								const next = payments.find((p) => p.payment_id === value)
								setAmount(next ? next.amount : '')
							}}
							withinPortal
						/>
						<NumberInput
							label="Refunded amount (VND)"
							value={amount}
							onChange={setAmount}
							min={0}
							max={payment?.amount}
							thousandsSeparator=","
							hideControls
							error={payment && amount === payment.amount ? undefined : amountError}
						/>
						<Textarea
							label={REFUND_NOTE_LABEL}
							placeholder="For example the bank transfer reference"
							value={note}
							onChange={(event) => setNote(event.currentTarget.value)}
							maxLength={NOTE_MAX_LENGTH}
							minRows={2}
							autosize
							withAsterisk
						/>
						<Checkbox
							label={LOCK_COURSE_LABEL}
							checked={locksCourse}
							onChange={(event) => setLocksCourse(event.currentTarget.checked)}
							disabled={saving}
						/>
						<Text size="xs" color="dimmed">
							{locksCourse ? (
								<>
									This records a refund the team has already sent outside the CMS, as Refunded. The
									learner is locked out of this course and can study again only by buying it again.
									It cannot be undone here.
								</>
							) : (
								<>
									This records a refund the team has already sent outside the CMS, as Refunded, and
									subtracts it from the revenue. The learner keeps access to this course. This payment
									cannot get another refund record later, and the lock cannot be added later.
								</>
							)}
						</Text>
					</>
				)}
				<ServerRefusal message={refusal} />
				<Group position="right">
					<Button variant="default" onClick={onClose} disabled={saving}>
						Cancel
					</Button>
					<Button
						color="red"
						onClick={submit}
						loading={saving}
						disabled={!learner || !payment || !!amountError || !note.trim()}
					>
						Save as Refunded
					</Button>
				</Group>
			</Stack>
		</Modal>
	)
}

/**
 * The Refunds list of Manage Learners: requests sent from the app and refunds
 * staff created, with the two actions on a pending request. onChanged tells the
 * learners table and the cards that a refund was recorded.
 */
function LearnerRefundsPanel({ onChanged }: { onChanged: () => void }) {
	const [status, setStatus] = useState('')
	const [pagination, setPagination] = useState<MRT_PaginationState>({ pageIndex: 1, pageSize: 20 })
	const [rows, setRows] = useState<CourseAiRefund[]>([])
	const [total, setTotal] = useState(0)
	const [loading, setLoading] = useState(false)
	const [refunding, setRefunding] = useState<CourseAiRefund | null>(null)
	const [rejecting, setRejecting] = useState<CourseAiRefund | null>(null)
	const [creating, setCreating] = useState(false)

	const loadRows = useCallback(async () => {
		setLoading(true)
		try {
			const response = await getCourseAiRefunds({
				status,
				page: pagination.pageIndex,
				limit: pagination.pageSize,
			})
			const data = unwrapObject<{ rows: CourseAiRefund[]; count: number }>(response)
			setRows(data?.rows || [])
			setTotal(Number(data?.count) || 0)
		} catch (error) {
			toast.error(errorMessage(error, 'Could not load refunds'))
			setRows([])
			setTotal(0)
		} finally {
			setLoading(false)
		}
	}, [status, pagination.pageIndex, pagination.pageSize])

	useEffect(() => {
		loadRows()
	}, [loadRows])

	const afterAction = () => {
		setRefunding(null)
		setRejecting(null)
		setCreating(false)
		loadRows()
		onChanged()
	}

	const allColumns = useMemo<MRT_ColumnDef<CourseAiRefund>[]>(
		() => [
			{ accessorKey: 'no', header: 'No.' },
			{
				id: 'learner',
				header: 'Learner',
				Cell: ({ row }) => (
					<Stack spacing={0}>
						<Text fw={600}>{row.original.name || '-'}</Text>
						<Text size="xs" color="dimmed">
							{row.original.phone || '-'}
						</Text>
					</Stack>
				),
			},
			{ accessorKey: 'course_name', header: 'Course', Cell: ({ row }) => row.original.course_name || '-' },
			{
				id: 'amount_paid',
				header: 'Amount Paid',
				Cell: ({ row }) => (
					<Stack spacing={0}>
						<Text>{formatVnd(row.original.amount_paid)}</Text>
						<Text size="xs" color="dimmed">
							{row.original.transaction_ref}
						</Text>
					</Stack>
				),
			},
			{
				id: 'reason',
				header: 'Reason',
				// A refund staff created shows the note staff typed; the learner's own request shows what the learner wrote.
				Cell: ({ row }) =>
					isStaffCreatedRefund(row.original) ? (
						<Stack spacing={0}>
							{row.original.note ? (
								<Text size="sm" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
									{row.original.note}
								</Text>
							) : null}
							<Text size={row.original.note ? 'xs' : 'sm'} color="dimmed">
								{REFUND_SOURCE_LABELS.staff_created}
							</Text>
						</Stack>
					) : (
						<Stack spacing={0}>
							<Text size="sm" fw={600}>
								{refundIssueText(row.original.type_of_issue)}
							</Text>
							<Text size="sm" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
								{row.original.reason || '-'}
							</Text>
						</Stack>
					),
			},
			{
				id: 'requested_at',
				header: 'Request Date',
				Cell: ({ row }) => formatDateTime(row.original.requested_at),
			},
			{
				id: 'status',
				header: 'Status',
				Cell: ({ row }) => {
					const refund = row.original
					const lockTag = refundLockTag(refund)
					return (
						<Stack spacing={2} align="flex-start">
							<Group spacing={4} noWrap>
								<Badge color={REFUND_STATUS_COLORS[refund.status] || 'gray'} variant="light">
									{REFUND_STATUS_LABELS[refund.status] || refund.status}
								</Badge>
								{lockTag ? (
									<Badge color={lockTag.color} variant="outline" size="sm">
										{lockTag.label}
									</Badge>
								) : null}
							</Group>
							{refund.status === 'refunded' ? (
								<Text size="xs">Refunded {formatVnd(refund.refunded_amount)}</Text>
							) : null}
							{refund.handled_at ? (
								<Text size="xs" color="dimmed">
									{refund.handled_by?.name || 'Staff'}, {formatDateTime(refund.handled_at)}
								</Text>
							) : null}
							{refund.note && !isStaffCreatedRefund(refund) ? (
								<Text size="xs" color="dimmed" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
									{refund.note}
								</Text>
							) : null}
						</Stack>
					)
				},
			},
		],
		[],
	)

	const columns = useMemo(
		() =>
			allColumns.map((column) => {
				const size = COLUMN_SIZES[String(column.id || column.accessorKey)]
				return size ? { ...column, size, minSize: size } : column
			}),
		[allColumns],
	)

	return (
		<Stack spacing="md">
			<Flex gap={10} wrap="wrap" align="flex-end">
				<Select
					label="Status"
					placeholder="All"
					clearable
					data={REFUND_STATUS_FILTER_OPTIONS}
					value={status || null}
					onChange={(value) => {
						setPagination((previous) => ({ ...previous, pageIndex: 1 }))
						setStatus(value || '')
					}}
					sx={{ width: 160 }}
				/>
				<Button ml="auto" color="green" leftIcon={<IconPlus size={16} />} onClick={() => setCreating(true)}>
					New refund
				</Button>
			</Flex>

			<TableComponent
				columns={columns}
				total={total}
				data={rows}
				pagination={pagination}
				loading={loading}
				syncQueryUrl={false}
				handleChangePage={(page: number) => setPagination((previous) => ({ ...previous, pageIndex: page }))}
				handleChangePageSize={(pageSize: number) => setPagination({ pageIndex: 1, pageSize })}
				hideMoreActions
				actionColumnSize={190}
				_renderCustomActions={(row: any) =>
					row.original.status === 'pending' ? (
						<Group spacing={6} noWrap>
							<Button size="xs" color="red" onClick={() => setRefunding(row.original)}>
								Refunded
							</Button>
							<Button size="xs" variant="default" onClick={() => setRejecting(row.original)}>
								Reject
							</Button>
						</Group>
					) : null
				}
			/>

			<RefundedDialog refund={refunding} onClose={() => setRefunding(null)} onDone={afterAction} />
			<RejectDialog refund={rejecting} onClose={() => setRejecting(null)} onDone={afterAction} />
			<NewRefundDialog opened={creating} onClose={() => setCreating(false)} onDone={afterAction} />
		</Stack>
	)
}

export default LearnerRefundsPanel
